// store/slices/branchAppointmentConfigSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  getAppointmentConfigThunk,
  updateAppointmentConfigThunk,
  getDayBuffersThunk,
  updateDayBuffersThunk,
  bulkUpdateDayBuffersThunk,
  getBlockedPeriodsThunk,
  addBlockedPeriodThunk,
  updateBlockedPeriodThunk,
  deleteBlockedPeriodThunk,
  toggleBlockedPeriodThunk,
  bulkAddBlockedPeriodsThunk,
  copyBlockedPeriodsThunk,
  compareBlockedPeriodsThunk,
  checkIsOpenThunk,
  getWorkingHoursThunk,
  getTodayScheduleThunk,
  getNextOpeningThunk,
  checkIsHolidayThunk,
  getHolidaysThunk,
  checkCapacityThunk,
  updateAppointmentStatsThunk,
  getExchangeChargesThunk,
  getNearbyBranchesThunk,
  getAppointmentTypesThunk,
  updateAppointmentTypeThunk,
  getAutoApprovalSettingsThunk,
  updateAutoApprovalSettingsThunk,
  getBookingWindowThunk,
  updateBookingWindowThunk,
  getNotificationSettingsThunk,
  updateNotificationSettingsThunk,
  getCancellationPolicyThunk,
  updateCancellationPolicyThunk,
  getSchedulingRulesThunk,
  updateSchedulingRulesThunk,
  getAvailableSlotsThunk,
  getWeeklySlotsThunk,
  checkSlotAvailabilityThunk,
  clearAppointmentConfigError,
  resetAppointmentConfig,
  setSelectedBranchCode,
  setConfigEditMode,
} from "../thunks/branchAppointmentConfigThunks";

const initialState = {
  // Selected branch
  selectedBranchCode: null,

  // Appointment Configuration
  appointmentConfig: null,
  isConfigEnabled: false,

  // Day Buffers
  dayBuffers: null,

  // Blocked Periods
  blockedPeriods: [],
  blockedPeriodsComparison: null,
  bulkOperationResults: null,

  // Scheduling Rules
  schedulingRules: null,

  // Working Hours & Schedule
  workingHours: null,
  todaySchedule: null,
  nextOpening: null,
  isOpen: null,
  isOpenCheckTime: null,

  // Holidays
  holidays: [],
  holidayCheck: null,

  // Capacity
  capacity: null,
  stats: null,

  // Exchange Charges
  exchangeCharges: null,

  // Nearby Branches
  nearbyBranches: [],
  nearbySearchParams: null,

  // Appointment Types
  appointmentTypes: [],

  // Auto-Approval Settings
  autoApprovalSettings: null,

  // Booking Window
  bookingWindow: null,

  // Notification Settings
  notificationSettings: null,

  // Cancellation Policy
  cancellationPolicy: null,

  // Available Slots
  availableSlots: [],
  weeklySlots: null,
  slotAvailability: null,
  selectedDate: null,
  selectedSlot: null,

  // Loading States
  loading: false,
  loadingConfig: false,
  loadingDayBuffers: false,
  loadingBlockedPeriods: false,
  loadingSchedulingRules: false,
  loadingSchedule: false,
  loadingHolidays: false,
  loadingCapacity: false,
  loadingNearby: false,
  loadingTypes: false,
  loadingAutoApproval: false,
  loadingBookingWindow: false,
  loadingNotifications: false,
  loadingCancellation: false,
  loadingSlots: false,
  loadingWeeklySlots: false,
  loadingBulkOperation: false,

  // Edit Mode
  isEditMode: false,
  hasUnsavedChanges: false,

  // Error Handling
  error: null,
  configError: null,
  bufferError: null,
  blockedPeriodError: null,
  scheduleError: null,
  capacityError: null,
  bulkOperationError: null,

  // Success Messages
  successMessage: null,

  // Constants
  appointmentTypeOptions: [
    "PRODUCT_CONSULTATION",
    "CUSTOM_DESIGN",
    "REPAIR_CONSULTATION",
    "APPRAISAL",
    "ENGAGEMENT_RING",
    "WEDDING_JEWELRY",
    "GENERAL_INQUIRY",
    "VIP_CLIENT",
    "COLLECTION_VIEWING",
  ],
  notificationChannels: ["SMS", "EMAIL", "WHATSAPP", "PUSH"],
  penaltyTypes: [
    "DEPOSIT_FORFEIT",
    "FEE",
    "POINTS_DEDUCTION",
    "NONE",
    "DEPOSIT_REQUIRED",
    "PHONE_VERIFY_ONLY",
    "TEMPORARY_BAN",
  ],
  blockedPeriodReasons: [
    "STAFF_MEETING",
    "LUNCH_BREAK",
    "INVENTORY_CHECK",
    "CLEANING",
    "TRAINING",
    "SYSTEM_MAINTENANCE",
    "BUSY_PERIOD",
    "CUSTOM",
  ],
};

const branchAppointmentConfigSlice = createSlice({
  name: "branchAppointmentConfig",
  initialState,
  reducers: {
    setHasUnsavedChanges: (state, action) => {
      state.hasUnsavedChanges = action.payload;
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
    },
    clearSlots: (state) => {
      state.availableSlots = [];
      state.slotAvailability = null;
      state.weeklySlots = null;
    },
    clearNearbyBranches: (state) => {
      state.nearbyBranches = [];
      state.nearbySearchParams = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearBulkOperationResults: (state) => {
      state.bulkOperationResults = null;
      state.bulkOperationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== APPOINTMENT CONFIGURATION ==========
      .addCase(getAppointmentConfigThunk.pending, (state) => {
        state.loadingConfig = true;
        state.configError = null;
      })
      .addCase(getAppointmentConfigThunk.fulfilled, (state, action) => {
        state.loadingConfig = false;
        const config =
          action.payload.data?.appointmentConfig ||
          action.payload.appointmentConfig;
        state.appointmentConfig = config;
        state.isConfigEnabled = config?.enabled || false;
      })
      .addCase(getAppointmentConfigThunk.rejected, (state, action) => {
        state.loadingConfig = false;
        state.configError = action.payload;
      })

      .addCase(updateAppointmentConfigThunk.pending, (state) => {
        state.loading = true;
        state.configError = null;
      })
      .addCase(updateAppointmentConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.appointmentConfig = action.payload.appointmentConfig;
        state.isConfigEnabled =
          action.payload.appointmentConfig?.enabled || false;
        state.hasUnsavedChanges = false;
        state.isEditMode = false;
        state.successMessage = "Appointment configuration updated successfully";
      })
      .addCase(updateAppointmentConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.configError = action.payload;
      })

      // ========== DAY BUFFERS ==========
      .addCase(getDayBuffersThunk.pending, (state) => {
        state.loadingDayBuffers = true;
        state.bufferError = null;
      })
      .addCase(getDayBuffersThunk.fulfilled, (state, action) => {
        state.loadingDayBuffers = false;
        state.dayBuffers = action.payload.data.dayBuffers;
      })
      .addCase(getDayBuffersThunk.rejected, (state, action) => {
        state.loadingDayBuffers = false;
        state.bufferError = action.payload;
      })

      .addCase(updateDayBuffersThunk.pending, (state) => {
        state.loading = true;
        state.bufferError = null;
      })
      .addCase(updateDayBuffersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.dayBuffers = action.payload.data.dayBuffers;
        state.successMessage = "Day buffers updated successfully";
      })
      .addCase(updateDayBuffersThunk.rejected, (state, action) => {
        state.loading = false;
        state.bufferError = action.payload;
      })

      .addCase(bulkUpdateDayBuffersThunk.pending, (state) => {
        state.loadingBulkOperation = true;
        state.bulkOperationError = null;
      })
      .addCase(bulkUpdateDayBuffersThunk.fulfilled, (state, action) => {
        state.loadingBulkOperation = false;
        state.bulkOperationResults = action.payload.data;
        state.successMessage = action.payload.message;
      })
      .addCase(bulkUpdateDayBuffersThunk.rejected, (state, action) => {
        state.loadingBulkOperation = false;
        state.bulkOperationError = action.payload;
      })

      // ========== BLOCKED PERIODS ==========
      .addCase(getBlockedPeriodsThunk.pending, (state) => {
        state.loadingBlockedPeriods = true;
        state.blockedPeriodError = null;
      })
      .addCase(getBlockedPeriodsThunk.fulfilled, (state, action) => {
        state.loadingBlockedPeriods = false;
        state.blockedPeriods = action.payload.data.blockedPeriods || [];
      })
      .addCase(getBlockedPeriodsThunk.rejected, (state, action) => {
        state.loadingBlockedPeriods = false;
        state.blockedPeriodError = action.payload;
      })

      .addCase(addBlockedPeriodThunk.pending, (state) => {
        state.loading = true;
        state.blockedPeriodError = null;
      })
      .addCase(addBlockedPeriodThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.blockedPeriods.push(action.payload.data.blockedPeriod);
        state.successMessage = "Blocked period added successfully";
      })
      .addCase(addBlockedPeriodThunk.rejected, (state, action) => {
        state.loading = false;
        state.blockedPeriodError = action.payload;
      })

      .addCase(updateBlockedPeriodThunk.pending, (state) => {
        state.loading = true;
        state.blockedPeriodError = null;
      })
      .addCase(updateBlockedPeriodThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = action.meta.arg.blockIndex;
        if (state.blockedPeriods[index]) {
          state.blockedPeriods[index] = action.payload.data.blockedPeriod;
        }
        state.successMessage = "Blocked period updated successfully";
      })
      .addCase(updateBlockedPeriodThunk.rejected, (state, action) => {
        state.loading = false;
        state.blockedPeriodError = action.payload;
      })

      .addCase(deleteBlockedPeriodThunk.pending, (state) => {
        state.loading = true;
        state.blockedPeriodError = null;
      })
      .addCase(deleteBlockedPeriodThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = action.meta.arg.blockIndex;
        state.blockedPeriods.splice(index, 1);
        state.successMessage = "Blocked period deleted successfully";
      })
      .addCase(deleteBlockedPeriodThunk.rejected, (state, action) => {
        state.loading = false;
        state.blockedPeriodError = action.payload;
      })

      .addCase(toggleBlockedPeriodThunk.pending, (state) => {
        state.loading = true;
        state.blockedPeriodError = null;
      })
      .addCase(toggleBlockedPeriodThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = action.meta.arg.blockIndex;
        if (state.blockedPeriods[index]) {
          state.blockedPeriods[index] = action.payload.data.blockedPeriod;
        }
        state.successMessage = action.payload.message;
      })
      .addCase(toggleBlockedPeriodThunk.rejected, (state, action) => {
        state.loading = false;
        state.blockedPeriodError = action.payload;
      })

      .addCase(bulkAddBlockedPeriodsThunk.pending, (state) => {
        state.loadingBulkOperation = true;
        state.bulkOperationError = null;
      })
      .addCase(bulkAddBlockedPeriodsThunk.fulfilled, (state, action) => {
        state.loadingBulkOperation = false;
        state.bulkOperationResults = action.payload.data;
        state.successMessage = action.payload.message;
      })
      .addCase(bulkAddBlockedPeriodsThunk.rejected, (state, action) => {
        state.loadingBulkOperation = false;
        state.bulkOperationError = action.payload;
      })

      .addCase(copyBlockedPeriodsThunk.pending, (state) => {
        state.loadingBulkOperation = true;
        state.bulkOperationError = null;
      })
      .addCase(copyBlockedPeriodsThunk.fulfilled, (state, action) => {
        state.loadingBulkOperation = false;
        state.bulkOperationResults = action.payload.data;
        state.successMessage = action.payload.message;
      })
      .addCase(copyBlockedPeriodsThunk.rejected, (state, action) => {
        state.loadingBulkOperation = false;
        state.bulkOperationError = action.payload;
      })

      .addCase(compareBlockedPeriodsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(compareBlockedPeriodsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.blockedPeriodsComparison = action.payload.data;
      })
      .addCase(compareBlockedPeriodsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== SCHEDULING RULES ==========
      .addCase(getSchedulingRulesThunk.pending, (state) => {
        state.loadingSchedulingRules = true;
      })
      .addCase(getSchedulingRulesThunk.fulfilled, (state, action) => {
        state.loadingSchedulingRules = false;
        state.schedulingRules = action.payload.data.schedulingRules;
      })
      .addCase(getSchedulingRulesThunk.rejected, (state, action) => {
        state.loadingSchedulingRules = false;
        state.error = action.payload;
      })

      .addCase(updateSchedulingRulesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSchedulingRulesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.schedulingRules = action.payload.data.schedulingRules;
        state.successMessage = "Scheduling rules updated successfully";
      })
      .addCase(updateSchedulingRulesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== WORKING HOURS & SCHEDULE ==========
      .addCase(checkIsOpenThunk.pending, (state) => {
        state.loadingSchedule = true;
      })
      .addCase(checkIsOpenThunk.fulfilled, (state, action) => {
        state.loadingSchedule = false;
        state.isOpen = action.payload.isOpen;
        state.isOpenCheckTime = action.payload.checkedAt;
      })
      .addCase(checkIsOpenThunk.rejected, (state, action) => {
        state.loadingSchedule = false;
        state.scheduleError = action.payload;
      })

      .addCase(getWorkingHoursThunk.pending, (state) => {
        state.loadingSchedule = true;
      })
      .addCase(getWorkingHoursThunk.fulfilled, (state, action) => {
        state.loadingSchedule = false;
        state.workingHours = action.payload.workingHours;
      })
      .addCase(getWorkingHoursThunk.rejected, (state, action) => {
        state.loadingSchedule = false;
        state.scheduleError = action.payload;
      })

      .addCase(getTodayScheduleThunk.pending, (state) => {
        state.loadingSchedule = true;
      })
      .addCase(getTodayScheduleThunk.fulfilled, (state, action) => {
        state.loadingSchedule = false;
        state.todaySchedule = action.payload.schedule;
      })
      .addCase(getTodayScheduleThunk.rejected, (state, action) => {
        state.loadingSchedule = false;
        state.scheduleError = action.payload;
      })

      .addCase(getNextOpeningThunk.pending, (state) => {
        state.loadingSchedule = true;
      })
      .addCase(getNextOpeningThunk.fulfilled, (state, action) => {
        state.loadingSchedule = false;
        state.nextOpening = action.payload.nextOpening;
      })
      .addCase(getNextOpeningThunk.rejected, (state, action) => {
        state.loadingSchedule = false;
        state.scheduleError = action.payload;
      })

      // ========== HOLIDAYS ==========
      .addCase(checkIsHolidayThunk.pending, (state) => {
        state.loadingHolidays = true;
      })
      .addCase(checkIsHolidayThunk.fulfilled, (state, action) => {
        state.loadingHolidays = false;
        state.holidayCheck = {
          isHoliday: action.payload.isHoliday,
          holiday: action.payload.holiday,
          date: action.payload.date,
        };
      })
      .addCase(checkIsHolidayThunk.rejected, (state, action) => {
        state.loadingHolidays = false;
        state.error = action.payload;
      })

      .addCase(getHolidaysThunk.pending, (state) => {
        state.loadingHolidays = true;
      })
      .addCase(getHolidaysThunk.fulfilled, (state, action) => {
        state.loadingHolidays = false;
        state.holidays = action.payload.holidays;
      })
      .addCase(getHolidaysThunk.rejected, (state, action) => {
        state.loadingHolidays = false;
        state.error = action.payload;
      })

      // ========== CAPACITY ==========
      .addCase(checkCapacityThunk.pending, (state) => {
        state.loadingCapacity = true;
      })
      .addCase(checkCapacityThunk.fulfilled, (state, action) => {
        state.loadingCapacity = false;
        state.capacity = {
          hasCapacity: action.payload.hasCapacity,
          maxAppointments: action.payload.maxAppointments,
          currentAppointments: action.payload.currentAppointments,
          remaining: action.payload.remaining,
          date: action.payload.date,
        };
      })
      .addCase(checkCapacityThunk.rejected, (state, action) => {
        state.loadingCapacity = false;
        state.capacityError = action.payload;
      })

      .addCase(updateAppointmentStatsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppointmentStatsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
      })
      .addCase(updateAppointmentStatsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== EXCHANGE CHARGES ==========
      .addCase(getExchangeChargesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getExchangeChargesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.exchangeCharges = action.payload.exchangeCharges;
      })
      .addCase(getExchangeChargesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== NEARBY BRANCHES ==========
      .addCase(getNearbyBranchesThunk.pending, (state) => {
        state.loadingNearby = true;
      })
      .addCase(getNearbyBranchesThunk.fulfilled, (state, action) => {
        state.loadingNearby = false;
        state.nearbyBranches = action.payload.branches;
        state.nearbySearchParams = {
          radiusKm: action.payload.radiusKm,
          count: action.payload.count,
        };
      })
      .addCase(getNearbyBranchesThunk.rejected, (state, action) => {
        state.loadingNearby = false;
        state.error = action.payload;
      })

      // ========== APPOINTMENT TYPES ==========
      .addCase(getAppointmentTypesThunk.pending, (state) => {
        state.loadingTypes = true;
      })
      .addCase(getAppointmentTypesThunk.fulfilled, (state, action) => {
        state.loadingTypes = false;
        state.appointmentTypes = action.payload.appointmentTypes;
      })
      .addCase(getAppointmentTypesThunk.rejected, (state, action) => {
        state.loadingTypes = false;
        state.error = action.payload;
      })

      .addCase(updateAppointmentTypeThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppointmentTypeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.appointmentTypes = action.payload.appointmentTypes;
        state.successMessage = "Appointment type updated successfully";
      })
      .addCase(updateAppointmentTypeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== AUTO-APPROVAL ==========
      .addCase(getAutoApprovalSettingsThunk.pending, (state) => {
        state.loadingAutoApproval = true;
      })
      .addCase(getAutoApprovalSettingsThunk.fulfilled, (state, action) => {
        state.loadingAutoApproval = false;
        state.autoApprovalSettings = action.payload.autoApproval;
      })
      .addCase(getAutoApprovalSettingsThunk.rejected, (state, action) => {
        state.loadingAutoApproval = false;
        state.error = action.payload;
      })

      .addCase(updateAutoApprovalSettingsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAutoApprovalSettingsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.autoApprovalSettings = action.payload.autoApproval;
        state.successMessage = "Auto-approval settings updated successfully";
      })
      .addCase(updateAutoApprovalSettingsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== BOOKING WINDOW ==========
      .addCase(getBookingWindowThunk.pending, (state) => {
        state.loadingBookingWindow = true;
      })
      .addCase(getBookingWindowThunk.fulfilled, (state, action) => {
        state.loadingBookingWindow = false;
        state.bookingWindow = action.payload.bookingWindow;
      })
      .addCase(getBookingWindowThunk.rejected, (state, action) => {
        state.loadingBookingWindow = false;
        state.error = action.payload;
      })

      .addCase(updateBookingWindowThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBookingWindowThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bookingWindow = action.payload.bookingWindow;
        state.successMessage = "Booking window updated successfully";
      })
      .addCase(updateBookingWindowThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== NOTIFICATIONS ==========
      .addCase(getNotificationSettingsThunk.pending, (state) => {
        state.loadingNotifications = true;
      })
      .addCase(getNotificationSettingsThunk.fulfilled, (state, action) => {
        state.loadingNotifications = false;
        state.notificationSettings = action.payload.notifications;
      })
      .addCase(getNotificationSettingsThunk.rejected, (state, action) => {
        state.loadingNotifications = false;
        state.error = action.payload;
      })

      .addCase(updateNotificationSettingsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateNotificationSettingsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationSettings = action.payload.notifications;
        state.successMessage = "Notification settings updated successfully";
      })
      .addCase(updateNotificationSettingsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== CANCELLATION POLICY ==========
      .addCase(getCancellationPolicyThunk.pending, (state) => {
        state.loadingCancellation = true;
      })
      .addCase(getCancellationPolicyThunk.fulfilled, (state, action) => {
        state.loadingCancellation = false;
        state.cancellationPolicy = action.payload.cancellationPolicy;
      })
      .addCase(getCancellationPolicyThunk.rejected, (state, action) => {
        state.loadingCancellation = false;
        state.error = action.payload;
      })

      .addCase(updateCancellationPolicyThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCancellationPolicyThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.cancellationPolicy = action.payload.cancellationPolicy;
        state.successMessage = "Cancellation policy updated successfully";
      })
      .addCase(updateCancellationPolicyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== AVAILABLE SLOTS ==========
      .addCase(getAvailableSlotsThunk.pending, (state) => {
        state.loadingSlots = true;
      })
      .addCase(getAvailableSlotsThunk.fulfilled, (state, action) => {
        state.loadingSlots = false;
        state.availableSlots =
          action.payload.data?.slots || action.payload.slots || [];
      })
      .addCase(getAvailableSlotsThunk.rejected, (state, action) => {
        state.loadingSlots = false;
        state.error = action.payload;
      })

      .addCase(getWeeklySlotsThunk.pending, (state) => {
        state.loadingWeeklySlots = true;
      })
      .addCase(getWeeklySlotsThunk.fulfilled, (state, action) => {
        state.loadingWeeklySlots = false;
        state.weeklySlots = action.payload.data;
      })
      .addCase(getWeeklySlotsThunk.rejected, (state, action) => {
        state.loadingWeeklySlots = false;
        state.error = action.payload;
      })

      .addCase(checkSlotAvailabilityThunk.pending, (state) => {
        state.loadingSlots = true;
      })
      .addCase(checkSlotAvailabilityThunk.fulfilled, (state, action) => {
        state.loadingSlots = false;
        state.slotAvailability = action.payload;
      })
      .addCase(checkSlotAvailabilityThunk.rejected, (state, action) => {
        state.loadingSlots = false;
        state.error = action.payload;
      })

      // ========== ACTIONS ==========
      .addCase(clearAppointmentConfigError, (state) => {
        state.error = null;
        state.configError = null;
        state.bufferError = null;
        state.blockedPeriodError = null;
        state.scheduleError = null;
        state.capacityError = null;
        state.bulkOperationError = null;
      })

      .addCase(resetAppointmentConfig, (state) => {
        return initialState;
      })

      .addCase(setSelectedBranchCode, (state, action) => {
        state.selectedBranchCode = action.payload;
        // Reset other data when branch changes
        state.appointmentConfig = null;
        state.dayBuffers = null;
        state.blockedPeriods = [];
        state.schedulingRules = null;
        state.workingHours = null;
        state.todaySchedule = null;
        state.holidays = [];
        state.capacity = null;
        state.availableSlots = [];
        state.weeklySlots = null;
      })

      .addCase(setConfigEditMode, (state, action) => {
        state.isEditMode = action.payload;
        if (!action.payload) {
          state.hasUnsavedChanges = false;
        }
      });
  },
});

export const {
  setHasUnsavedChanges,
  setSelectedDate,
  setSelectedSlot,
  clearSlots,
  clearNearbyBranches,
  clearSuccessMessage,
  clearBulkOperationResults,
} = branchAppointmentConfigSlice.actions;

export default branchAppointmentConfigSlice.reducer;
